import { useSyncExternalStore } from 'react'

/**
 * A kitchen's paid, unaccepted orders.
 *
 * A single bell is easy to miss over an extractor fan or from the next room,
 * so while any paid order is waiting the bell repeats until the chef accepts
 * it (or cancels it). The chef can silence the repeat for a few minutes; a new
 * order, or the silence running out while the order still waits, rings again.
 * The server repeats the push on the same cadence for a chef whose app is
 * closed (notification service nudgeUnacceptedOrders).
 */
export const RING_EVERY_MS = 8_000
export const SILENCE_MS = 3 * 60_000

type Row = Record<string, unknown>

export interface IncomingOrdersState {
  /** Sorted ids of the chef's paid orders. */
  waiting: readonly string[]
  silencedUntil: number
  /** The orders that were waiting when the chef silenced the bell. */
  silencedIds: readonly string[]
}

const norm = (value: unknown) => String(value ?? '').trim().toLowerCase()

export const waitingOrderIds = (rows: readonly Row[], profileId: string): string[] => {
  const me = norm(profileId)
  if (!me) return []
  return rows
    .filter((row) => norm(row.merchant_profile_id) === me && norm(row.status).replace(/^order_status_/, '') === 'paid')
    .map((row) => String(row.id))
    .sort()
}

/** True while an order waits that the chef has not silenced, or the silence ran out. */
export const shouldRing = (state: IncomingOrdersState, now: number): boolean => {
  if (state.waiting.length === 0) return false
  if (now >= state.silencedUntil) return true
  return state.waiting.some((id) => !state.silencedIds.includes(id))
}

const EMPTY: IncomingOrdersState = { waiting: [], silencedUntil: 0, silencedIds: [] }
let state = EMPTY
const listeners = new Set<() => void>()
const publish = (next: IncomingOrdersState) => {
  state = next
  listeners.forEach((listener) => listener())
}

export const incomingOrders = {
  subscribe(listener: () => void) {
    listeners.add(listener)
    return () => { listeners.delete(listener) }
  },
  snapshot: () => state,
  setWaiting(ids: readonly string[]) {
    if (ids.length === state.waiting.length && ids.every((id, i) => id === state.waiting[i])) return
    publish(ids.length === 0 ? EMPTY : { ...state, waiting: [...ids] })
  },
  silence(now = Date.now()) {
    publish({ ...state, silencedUntil: now + SILENCE_MS, silencedIds: [...state.waiting] })
  },
  reset() {
    publish(EMPTY)
  },
}

export const useIncomingOrders = () =>
  useSyncExternalStore(incomingOrders.subscribe, incomingOrders.snapshot, incomingOrders.snapshot)
